'use client';

import React, { useId } from 'react';
import { cn, composeTailwindRenderProps } from '@utils';
import {
	Select as AriaSelect,
	SelectProps as AriaSelectProps,
	Button,
	ListBoxItemProps,
	SelectValue,
	ValidationResult,
} from 'react-aria-components';
import { tv } from 'tailwind-variants';

import { ChevronDown } from '../icons';
import {
	Description,
	FieldError,
	fieldStyle,
	FieldSuccess,
	FieldVariant,
	FieldWidth,
	iconStyle,
	Label,
} from './Field';
import { DropdownItem, DropdownSection, DropdownSectionProps, ListBox } from './ListBox';
import { Popover } from './Popover';

interface SelectProps<T extends object> extends Omit<AriaSelectProps<T>, 'children'> {
	label?: string;
	hint?: string;
	hintChildren?: React.ReactNode;
	successMessage?: string;
	errorMessage?: string | ((validation: ValidationResult) => string);
	isValid?: boolean;
	items?: Iterable<T>;
	isLoading?: boolean;
	children: React.ReactNode | ((item: T) => React.ReactNode);
	theme?: 'light' | 'dark';
	variant?: FieldVariant;
	width?: FieldWidth;
	focusOnOpen?: boolean;
}

const Select = <T extends object>({
	label,
	hint,
	hintChildren,
	errorMessage,
	isValid,
	successMessage,
	children,
	theme,
	items,
	isLoading,
	variant,
	width,
	focusOnOpen,
	...props
}: SelectProps<T>) => {
	const id = useId();

	const handleOpenChange = (isOpen: boolean) => {
		props.onOpenChange?.(isOpen);

		// Scroll to the selected item when the select dropdown is opened
		if (isOpen && focusOnOpen && props.selectedKey) {
			requestAnimationFrame(() => {
				const selectedElement = document.querySelector(`[data-key="${props.selectedKey}"]`);
				selectedElement?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
			});
		}
	};

	const selectValueStyle = tv({
		base: 'block w-full truncate text-left text-sm',
		variants: {
			isPlaceholder: {
				true: '',
			},
			theme: {
				light: 'text-light-text',
				dark: 'text-dark-text',
			},
		},
		compoundVariants: [
			{
				isPlaceholder: true,
				theme: 'light',
				className: 'text-light-text-lighter',
			},
			{
				isPlaceholder: true,
				theme: 'dark',
				className: 'text-dark-text-lighter',
			},
		],
		defaultVariants: {
			theme: 'light',
		},
	});

	return (
		<AriaSelect
			id={id}
			{...props}
			onOpenChange={handleOpenChange}
			className={composeTailwindRenderProps(props.className, 'group flex flex-col gap-2')}
		>
			{({ isDisabled, isFocused, isInvalid }) => (
				<>
					{label && <Label isRequired={props.isRequired}>{label}</Label>}
					{hint && <Description>{hint}</Description>}
					{hintChildren && hintChildren}
					{errorMessage && <FieldError>{errorMessage}</FieldError>}
					{isValid && successMessage && <FieldSuccess>{successMessage}</FieldSuccess>}
					<Button
						className={cn(
							fieldStyle({
								variant,
								width,
								theme,
								isInvalid,
								isValid,
								isDisabled,
								isFocusWithin: isFocused,
							}),
							'flex cursor-pointer items-center justify-between gap-2 disabled:cursor-not-allowed',
						)}
					>
						<div className="min-w-0 flex-1 overflow-hidden">
							<SelectValue>
								{({ defaultChildren, isPlaceholder }) => (
									<p className={selectValueStyle({ theme, isPlaceholder })}>
										{isPlaceholder ? props.placeholder : defaultChildren}
									</p>
								)}
							</SelectValue>
						</div>
						<ChevronDown
							aria-hidden
							className={cn(
								iconStyle({
									theme,
									isDisabled,
									isFocusWithin: isFocused,
									isInvalid,
								}),
								'flex-shrink-0',
							)}
						/>
					</Button>
					<Popover className="w-[var(--trigger-width)] border-none" theme={theme}>
						<ListBox items={items} className="max-h-[inherit] overflow-auto p-1 outline-none">
							{items && Array.from(items).length > 0 ? (
								children
							) : isLoading ? (
								<SelectItem isDisabled>
									<p>Loading...</p>
								</SelectItem>
							) : (
								<SelectItem isDisabled>No options</SelectItem>
							)}
						</ListBox>
					</Popover>
				</>
			)}
		</AriaSelect>
	);
};

const SelectItem = ({
	theme,
	hideIcon,
	...props
}: ListBoxItemProps & { theme?: 'light' | 'dark'; hideIcon?: boolean }) => {
	return <DropdownItem theme={theme} hideIcon={hideIcon} {...props} />;
};

const SelectSection = <T extends object>(props: DropdownSectionProps<T>) => {
	return <DropdownSection {...props} />;
};

Select.Item = SelectItem;
Select.Section = SelectSection;

export { Select };
export type { SelectProps };
